import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Play, X, MessageCircle } from "lucide-react";
import { ARTISTS, ARTIST_CATEGORIES, whatsappLink } from "../data/content";
import { Reveal, SectionLabel } from "./Reveal";

export const Artisti = () => {
  const [active, setActive] = useState("all");
  const [videoArtist, setVideoArtist] = useState(null);
  const visible =
    active === "all" ? ARTISTS : ARTISTS.filter((a) => a.category === active);

  return (
    <section
      id="artisti"
      data-testid="artists-section"
      className="relative py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <SectionLabel>Il Roster</SectionLabel>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-5 max-w-3xl leading-tight">
            Artisti &amp; spettacoli,{" "}
            <span className="italic text-gold-gradient">
              pronti a salire sul palco
            </span>
          </h2>
        </Reveal>

        <Reveal delay={0.15}>
          <div
            data-testid="artist-filter-tabs"
            className="mt-10 flex flex-wrap gap-2.5"
          >
            {ARTIST_CATEGORIES.map((c) => (
              <button
                key={c.id}
                data-testid={`artist-filter-tab-${c.id}`}
                onClick={() => setActive(c.id)}
                className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 border ${
                  active === c.id
                    ? "bg-[#E6C594] text-[#08070B] border-[#E6C594]"
                    : "border-[#282338] text-[#A19CAE] hover:text-[#F4F1EE] hover:border-[#E6C594]/40"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {visible.map((a, i) => {
              const Tag = a.video ? motion.div : motion.a;
              const linkProps = a.video
                ? { onClick: () => setVideoArtist(a), role: "button" }
                : { href: whatsappLink(), target: "_blank", rel: "noopener noreferrer" };
              return (
                <Tag
                  layout
                  key={a.id}
                  data-testid={`artist-card-${a.id}`}
                  {...linkProps}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.5, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                  className="card-spotlight group relative rounded-2xl overflow-hidden border border-[#282338] bg-[#121018] cursor-pointer"
                >
                  <div className="relative h-72 overflow-hidden">
                    <img
                      src={a.image}
                      alt={a.name}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#121018] via-transparent to-transparent" />
                    <span className="absolute top-4 left-4 font-mono-label text-[10px] tracking-[0.25em] bg-[#08070B]/80 backdrop-blur px-3 py-1.5 rounded-full text-[#E6C594] border border-[#E6C594]/25">
                      {a.tag}
                    </span>
                    {a.video && (
                      <span
                        data-testid={`artist-video-play-${a.id}`}
                        className="absolute inset-0 flex items-center justify-center"
                      >
                        <span className="flex items-center gap-2 rounded-full bg-[#D92B34]/90 backdrop-blur text-white text-xs font-semibold tracking-wider px-5 py-3 shadow-[0_10px_40px_-10px_rgba(217,43,52,0.8)] transition-transform duration-300 group-hover:scale-110">
                          <Play size={16} fill="currentColor" />
                          GUARDA IL LIVE
                        </span>
                      </span>
                    )}
                  </div>
                  <div className="p-6 flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-serif-display text-xl sm:text-2xl font-semibold">
                        {a.name}
                      </h3>
                      <p className="mt-2 text-sm text-[#A19CAE] leading-relaxed">
                        {a.role}
                      </p>
                    </div>
                    <span className="shrink-0 mt-1 rounded-full border border-[#282338] p-2.5 text-[#A19CAE] transition-all duration-300 group-hover:bg-[#D92B34] group-hover:border-[#D92B34] group-hover:text-white">
                      {a.video ? <Play size={18} /> : <ArrowUpRight size={18} />}
                    </span>
                  </div>
                </Tag>
              );
            })}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {videoArtist && (
          <motion.div
            data-testid="video-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-[#08070B]/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-10"
            onClick={() => setVideoArtist(null)}
          >
            <motion.div
              initial={{ scale: 0.92, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-4xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="video-modal-close"
                onClick={() => setVideoArtist(null)}
                className="absolute -top-12 right-0 text-[#A19CAE] hover:text-[#E6C594] transition-colors"
                aria-label="Chiudi video"
              >
                <X size={28} />
              </button>
              <p className="font-mono-label text-[10px] tracking-[0.3em] text-[#E6C594] mb-3">
                LIVE — {videoArtist.name.toUpperCase()}
              </p>
              <video
                data-testid="video-modal-player"
                src={videoArtist.video}
                poster={videoArtist.image}
                controls
                autoPlay
                playsInline
                className="w-full rounded-2xl border border-[#282338] shadow-[0_40px_120px_-30px_rgba(230,197,148,0.2)]"
              />
              <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-sm text-[#A19CAE]">{videoArtist.role}</p>
                <a
                  data-testid="video-modal-whatsapp"
                  href={whatsappLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-[#E6C594] text-[#08070B] text-sm font-semibold px-6 py-3 hover:bg-[#FFF0D0] transition-colors shrink-0"
                >
                  <MessageCircle size={16} />
                  {videoArtist.category === "band" ? "Prenota questa band" : "Prenota questo artista"}
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
